import styles from './CreditSummary.module.css';

export interface CreditSummaryProps {
  allowance: number;
  spent: number;
  available: number;
  periodStart: Date;
  periodEnd: Date;
  now: Date;
}

const number = new Intl.NumberFormat();
const monthDay = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long' });
const shortMonthDay = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });

/** This month's credits: what is left, what went, and when it resets. */
export function CreditSummary({
  allowance,
  spent,
  available,
  periodStart,
  periodEnd,
  now,
}: CreditSummaryProps) {
  const share = allowance > 0 ? Math.min(1, available / allowance) : 0;
  const tone = share > 0.25 ? styles.good : share > 0.1 ? styles.low : styles.out;
  const daysLeft = Math.max(0, Math.ceil((periodEnd.getTime() - now.getTime()) / 86_400_000));

  return (
    <dl className={styles.tiles}>
      <div className={`${styles.tile} ${styles.hero}`}>
        <dt>Credits left</dt>
        <dd className={styles.value}>{number.format(available)}</dd>
        <dd className={styles.detail}>
          <span
            className={styles.meter}
            role="meter"
            aria-label="Credits left this month"
            aria-valuemin={0}
            aria-valuemax={allowance}
            aria-valuenow={available}
          >
            <span className={tone} style={{ width: `${share * 100}%` }} />
          </span>
          <span className={styles.wide}>of {number.format(allowance)} this month</span>
          <span className={styles.narrow}>
            of {number.format(allowance)} · resets {shortMonthDay.format(periodEnd)}
          </span>
        </dd>
      </div>
      <div className={styles.tile}>
        <dt>Spent this month</dt>
        <dd className={styles.value}>{number.format(spent)}</dd>
        <dd className={styles.detail}>
          <span className={styles.wide}>credits since {monthDay.format(periodStart)}</span>
          <span className={styles.narrow}>since {shortMonthDay.format(periodStart)}</span>
        </dd>
      </div>
      <div className={`${styles.tile} ${styles.resets}`}>
        <dt>Allowance resets</dt>
        <dd className={styles.value}>{monthDay.format(periodEnd)}</dd>
        <dd className={styles.detail}>
          {daysLeft === 1 ? 'tomorrow' : `in ${number.format(daysLeft)} days`}
        </dd>
      </div>
    </dl>
  );
}
